import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

interface PortingMeta {
  customerName?: string;
  address?: string;
  cityStateZip?: string;
  provider?: string;
  requestedDeliveryDate?: string;
  requestedDeliveryTime?: string;
}

interface PortingRecord {
  uid: string;
  companyId: string;
  createdBy: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | string;
  url: string;
  createdAt: string;
  updatedAt: string;
  meta: PortingMeta;
}

interface Props {
  ticket: PortingRecord;
}

export const Ticket = ({ ticket }: Props) => {
  const {
    status = "PENDING",
    url = "",
    createdAt,
    meta = {},
  } = ticket || {};

  const {
    customerName = "Unknown",
    address = "",
    cityStateZip = "",
    provider = "",
    requestedDeliveryDate = "",
    requestedDeliveryTime = "",
  } = meta;

  const statusColor =
    status === "PENDING"
      ? "warning"
      : status === "APPROVED"
      ? "success"
      : status === "REJECTED"
      ? "error"
      : "default";

  return (
    <Box
      sx={{
        maxWidth: 600,
        height: "100%",
        mx: "auto",
        p: 3,
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Porting Request Status
      </Typography>

      <Card
        sx={{
          borderRadius: 2,
          border: "1px solid var(--fx-surface-border, #E2E8F0)",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6" fontWeight={600}>
              {customerName}
            </Typography>
            <Chip
              label={status}
              color={statusColor as any}
              sx={{ textTransform: "capitalize", fontWeight: 500 }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={1}>
            {address && cityStateZip ? `${address}, ${cityStateZip}` : address}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary">
            Service Provider
          </Typography>
          <Typography variant="body1" mb={1}>
            {provider}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            Requested Delivery
          </Typography>
          <Typography variant="body1" mb={2}>
            {requestedDeliveryDate && requestedDeliveryTime
              ? `${requestedDeliveryDate} at ${requestedDeliveryTime}`
              : requestedDeliveryDate || "Not specified"}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(createdAt).toLocaleString()}
            </Typography>
            {url && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                href={url}
                target="_blank"
              >
                View PDF
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Box textAlign="center" mt={3}>
        <Typography variant="body2" color="text.secondary">
          Your porting request is currently{" "}
          <strong
            style={{
              color:
                status === "APPROVED"
                  ? "#2e7d32"
                  : status === "REJECTED"
                  ? "#d32f2f"
                  : "#ed6c02",
            }}
          >
            {status.toLowerCase()}
          </strong>
          . We will notify you once it has been processed.
        </Typography>
      </Box>
    </Box>
  );
};
